/**
 * Store Zustand pour l'état du Live Shopping
 *
 * Permet de persister l'état de la session live même lors de la navigation
 * entre les onglets du workspace (POS, Préparation, Live)
 */
import { create } from 'zustand';

export type LiveSessionStatus =
  | 'idle'           // Pas de session active
  | 'connecting'     // Connexion en cours
  | 'waiting'        // Session ouverte, en attente de client
  | 'customer_requesting' // Client demande une session
  | 'active'         // Session active avec client
  | 'ending';        // Fermeture en cours

interface LiveShoppingStore {
  // État de la session
  sessionId: string | null;
  sessionStatus: LiveSessionStatus;

  // File d'attente
  customersInQueue: number;
  pendingCustomerId: string | null;
  pendingCustomerName: string | null;

  // Connexions
  socketConnected: boolean;
  rtcConnected: boolean;

  // Vendeur actif en live
  liveVendorId: string | null;
  liveVendorName: string | null;

  // Actions
  setSessionId: (id: string | null) => void;
  setSessionStatus: (status: LiveSessionStatus) => void;
  setCustomersInQueue: (count: number) => void;
  setPendingCustomer: (id: string | null, name: string | null) => void;
  setSocketConnected: (connected: boolean) => void;
  setRtcConnected: (connected: boolean) => void;
  setLiveVendor: (id: string | null, name: string | null) => void;

  // Actions combinées
  openSession: (sessionId: string, vendorId: string, vendorName: string) => void;
  closeSession: () => void;
  customerJoined: (customerId: string, customerName: string) => void;
  customerLeft: () => void;

  // Helpers
  isLiveActive: () => boolean;
  hasCustomerWaiting: () => boolean;
  canStartLive: () => boolean;

  // Reset complet
  resetLiveShopping: () => void;
}

export const useLiveShoppingStore = create<LiveShoppingStore>((set, get) => ({
  // État initial
  sessionId: null,
  sessionStatus: 'idle',
  customersInQueue: 0,
  pendingCustomerId: null,
  pendingCustomerName: null,
  socketConnected: false,
  rtcConnected: false,
  liveVendorId: null,
  liveVendorName: null,

  // Actions simples
  setSessionId: (id) => set({ sessionId: id }),

  setSessionStatus: (status) => set({ sessionStatus: status }),

  setCustomersInQueue: (count) => set({ customersInQueue: count }),

  setPendingCustomer: (id, name) => set({
    pendingCustomerId: id,
    pendingCustomerName: name,
  }),

  setSocketConnected: (connected) => set({ socketConnected: connected }),

  setRtcConnected: (connected) => set({ rtcConnected: connected }),

  setLiveVendor: (id, name) => set({
    liveVendorId: id,
    liveVendorName: name,
  }),

  // Actions combinées
  openSession: (sessionId, vendorId, vendorName) => {
    set({
      sessionId,
      sessionStatus: 'waiting',
      liveVendorId: vendorId,
      liveVendorName: vendorName,
      customersInQueue: 0,
      pendingCustomerId: null,
      pendingCustomerName: null,
    });
  },

  closeSession: () => {
    set({
      sessionId: null,
      sessionStatus: 'idle',
      customersInQueue: 0,
      pendingCustomerId: null,
      pendingCustomerName: null,
      socketConnected: false,
      rtcConnected: false,
      liveVendorId: null,
      liveVendorName: null,
    });
  },

  customerJoined: (customerId, customerName) => {
    set({
      sessionStatus: 'active',
      pendingCustomerId: customerId,
      pendingCustomerName: customerName,
    });
  },

  customerLeft: () => {
    set({
      sessionStatus: 'waiting',
      pendingCustomerId: null,
      pendingCustomerName: null,
      rtcConnected: false,
    });
  },

  // Helpers
  isLiveActive: () => {
    const state = get();
    return state.sessionStatus === 'active' || state.sessionStatus === 'waiting';
  },

  hasCustomerWaiting: () => {
    const state = get();
    return state.sessionStatus === 'customer_requesting' || state.customersInQueue > 0;
  },

  canStartLive: () => {
    const state = get();
    return state.sessionStatus === 'idle' && state.liveVendorId === null;
  },

  // Reset complet
  resetLiveShopping: () => {
    set({
      sessionId: null,
      sessionStatus: 'idle',
      customersInQueue: 0,
      pendingCustomerId: null,
      pendingCustomerName: null,
      socketConnected: false,
      rtcConnected: false,
      liveVendorId: null,
      liveVendorName: null,
    });
  },
}));
